import { useSelector } from 'react-redux';
import { useActualIncidentId } from './commonHooks';
import { selectUserGuid } from 'slices/commonSelectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { toast } from 'react-toastify';
import { toastConfig } from 'assets/data/config';
import { Status } from '../planningView/widgets/AssignmentList/AssignmentList';

export type PlannedResource = {
  id: string;
  name: string;
  leader: string;
  numberOfPersons: number;
  contactInfo: string;
  reportingInfo: string;
  has204a: boolean;
  resourceBranch?: string;
  resourceDivision?: string;
  resourceGroup?: string;
  resourceStrikeTeam?: string;
  resourceTaskForce?: string;
  resourceAssignmentLocation?: string;
  resourceSpecialInstructions?: string;
  resourceSafetyPlanLocation?: string;
};

export type PlannedResourceRow = PlannedResource & {
  name: string;
  type?: string;
  walaId: string | null;
};

export type WalaLog = {
  id: string;
  status: Status;
  proposedStatus: Status;
  updatedAt: string;
  updatedByUserName: string;
  updatedByEmail: string;
}[];

type CommonWorkAssignmentListAttachmentBody = {
  proposedStatus?: Status;
  sectionChief?: string;
  branchDirector?: string;
  divisionGroupSupervisor?: string;
  workGroupId?: string;
  branch?: string;
  division?: string;
  walaGroup?: string;
  stagingArea?: string;
  workAssignmentIds?: string[];
  resources?: string[]; // intermediary step: I want to merge resources and plannedResources
  plannedResources?: PlannedResource[]; // intermediary step: I want to merge resources and plannedResources
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  specialInstructions?: string;
};

export type WorkAssignmentListAttachmentCreateBody =
  CommonWorkAssignmentListAttachmentBody & {
    workGroupId: string; // required for creation
    periodNumber: number; // required for creation
  };

export type WorkAssignmentListAttachmentUpdateBody =
  CommonWorkAssignmentListAttachmentBody & {
    walaId: string; // required for update
  };

export type WorkAssignmentListAttachmentData = {
  id: string;
  walaId: string;
  incidentId: string;
  userGuid: string;
  workGroupId: string;
  branch: string;
  division: string;
  walaGroup: string;
  stagingArea: string;
  status: Status;
  proposedStatus: Status;
  sectionChief?: string;
  branchDirector?: string;
  divisionGroupSupervisor?: string;
  workGroupName: string;
  resources: string[];
  plannedResources: PlannedResource[];
  medicalCommunication?: string;
  evacuationCommunication?: string;
  otherCommunication?: string;
  operationalPeriodNumber: number;
  specialInstructions?: string;
};

export const keys = {
  base: 'incident_workAssignmentListAttachment',
  get all() {
    return [keys.base];
  },
  list: (incidentId: string, userId: string) => [keys.base, incidentId, userId],
} as const;

export const useWalaLog = (walaId: string) => {
  const incidentId = useActualIncidentId();
  return useQuery<WalaLog>({
    queryKey: ['walaLog', walaId],
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/log`
      );
      return data;
    },
    enabled: !!walaId && !!incidentId,
  });
};

export const useWorkAssignmentListAttachments = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  return useQuery<WorkAssignmentListAttachmentData[]>({
    queryKey: keys.list(incidentId, user_guid),
    queryFn: async () => {
      if (!incidentId) return [];
      const { data } = await axios.get(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}`
      );
      return data;
    },
    enabled: !!incidentId,
  });
};

export const useCreateWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: WorkAssignmentListAttachmentCreateBody) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      toast.success(
        'Work Assignment List Attachment created successfully.',
        toastConfig
      );
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useUpdateWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: WorkAssignmentListAttachmentUpdateBody) => {
      return await axios.put(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${data.walaId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      toast.success(
        'Work Assignment List Attachment updated successfully.',
        toastConfig
      );
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useApproveWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${attachmentId}/approve`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useDenyWorkAssignmentListAttachment = () => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return await axios.post(
        `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${attachmentId}/deny`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
    },
    onError: (error: any) => {
      if (!incidentId) {
        toast.error(
          "You don't have any incidents in your organization. Please create one to proceed.",
          toastConfig
        );
      } else {
        toast.error(error.message, toastConfig);
      }
    },
  });
  return mutation;
};

export const useUpdatePlannedResource = (plannedResourceId: string) => {
  const incidentId = useActualIncidentId();
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: PlannedResource) => {
      return await axios.put(
        `${config.apiGateway.ics_planned_resource}/${plannedResourceId}`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(incidentId, user_guid),
      });
      toast.success(
        'Work Assignment List Attachment updated successfully.',
        toastConfig
      );
    },
    onError: (error: any) => {
      toast.error(error.message, toastConfig);
    },
  });
  return mutation;
};

export const ICS_204_CG_FORM_NAME = 'ICS_204_CG';

export async function postExportWorkAssignmentListAttachment(
  incidentId: string,
  periodNumber: number,
  walaId: string
): Promise<string> {
  const response = await axios.post(
    `${config.apiGateway.ics_work_assignment_list_attachment}/${incidentId}/${walaId}/export`,
    {
      periodNumber,
      skipWorkspaceUpload: true,
    }
  );
  return response.data;
}
